
import { Injectable } from '@angular/core';
import { LocalStorageProvider } from './storage.service';
import { MainService } from './main.service';
import { API_ROUTES } from '../_constants/api-route.constant';
import { firstValueFrom } from 'rxjs';
import heic2any from 'heic2any';


@Injectable({
  providedIn: 'root'
})
export class UploadService {

  currentUser: any;
  constructor(
    private storageService: LocalStorageProvider,
    private http: MainService) {
    this.currentUser = this.storageService.getItem('CurrentUser');
  }

  determineFileCategory(file: File): 'images' | 'videos' | 'others' {
    if (file.type.startsWith('image/')) {
      return 'images';
    } else if (file.type.startsWith('video/')) {
      return 'videos';
    } else {
      return 'others';
    }
  }

  async uploadFile(fileOrFiles: File | File[]): Promise<string | string[] | undefined> {
    try {

      // ---------- SINGLE FILE ----------
      if (fileOrFiles instanceof File) {
        const normalizedFile = await this.convertHeicIfNeeded(fileOrFiles);

        const response = await firstValueFrom(
          this.http.postFile(`${API_ROUTES.ACCOUNT.uploadImage}`, normalizedFile)
        );

        return response?.data?.url;
      }

      // ---------- MULTIPLE FILES ----------
      const uploadPromises = fileOrFiles.map(async (file) => {
        try {
          const normalizedFile = await this.convertHeicIfNeeded(file);

          const response = await firstValueFrom(
            this.http.postFile(`${API_ROUTES.ACCOUNT.uploadImage}`, normalizedFile)
          );

          return response?.data?.url;
        } catch {
          return undefined;
        }
      });

      const results = await Promise.all(uploadPromises);
      return results.filter((url): url is string => !!url);

    } catch (err) {
      console.error('Upload failed:', err);
      return undefined;
    }
  }

  getFileTypeFromS3Url(fileUrl: string): 'images' | 'videos' | 'others' {
    const extension = fileUrl.split('.').pop()?.toLowerCase() || '';
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'heic', 'heif'];
    const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'flv', 'wmv'];

    if (imageExtensions.includes(extension)) return 'images';
    if (videoExtensions.includes(extension)) return 'videos';
    return 'others';
  }

  async deleteFile(fileName: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post(`${API_ROUTES.ACCOUNT.deleteImage}`, { url: fileName })
      )
      return response?.status;
    } catch (err) {
      return false;
    }
  }
  private async convertHeicIfNeeded(file: File): Promise<File> {
    const isHeic =
      file.type === 'image/heic' ||
      file.type === 'image/heif' ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif');

    if (!isHeic) {
      return file; // no conversion needed
    }

    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9
    });

    return new File(
      [convertedBlob as Blob],
      file.name.replace(/\.(heic|heif)$/i, '.jpg'),
      { type: 'image/jpeg' }
    );
  }

}

